import { Link } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PublicHeader } from '../components/PublicHeader'
import { PublicFooter } from '../components/PublicFooter'
import { Reveal } from '../components/Reveal'
import { IconBadge } from '../components/IconBadge'

/**
 * Landing-визитка EdPad Учитель.
 * Структура (по аналогии с edpadclass.ru, но lean):
 * 1. Hero
 * 2. Возможности (12 LLM-инструментов grid)
 * 3. Как работает (3 шага)
 * 4. Преимущества (offline / 152-ФЗ / школьный модуль)
 * 5. Тарифы
 * 6. FAQ (accordion)
 * 7. Footer + CTA
 */
export function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />
      <HeroSection />
      <FeaturesSection />
      <HowItWorksSection />
      <AdvantagesSection />
      <PricingSection />
      <FaqSection />
      <CtaSection />
      <PublicFooter />
    </div>
  )
}

// ── 1. Hero ──────────────────────────────────────────────────────────────────

function HeroSection() {
  const { t } = useTranslation()
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-primary-soft via-white to-purple-50 py-14 sm:py-20 md:py-24">
      {/* Animated decorative blobs */}
      <div className="absolute top-10 right-10 w-72 h-72 bg-primary/15 rounded-full blur-3xl animate-blob-slow" />
      <div
        className="absolute bottom-10 left-10 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl animate-blob-slow"
        style={{ animationDelay: '-7s' }}
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 text-center">
        <div className="inline-block bg-white border border-primary/20 rounded-full px-4 py-1 text-xs font-medium text-primary mb-5 animate-fade-in">
          🇷🇺 {t('landingPage.heroBadge')}
        </div>

        <h1
          className="text-[26px] sm:text-4xl md:text-6xl font-extrabold text-gray-900 mb-5 md:mb-6 leading-[1.15] tracking-tight animate-fade-up px-1"
          style={{ animationDelay: '80ms' }}
        >
          {t('landingPage.heroTitle1')}<br />
          {t('landingPage.heroTitle2')}{' '}
          <span className="bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
            {t('landingPage.heroTitleAccent')}
          </span>
        </h1>

        <p
          className="text-sm sm:text-lg md:text-xl text-gray-600 mb-8 md:mb-10 max-w-2xl mx-auto animate-fade-up px-1 leading-relaxed"
          style={{ animationDelay: '180ms' }}
        >
          {t('landingPage.heroSubtitle1')}
          <strong className="text-gray-800"> {t('landingPage.heroSubtitleStrong')}</strong> {t('landingPage.heroSubtitle2')}
        </p>

        <div
          className="flex flex-col sm:flex-row gap-3 justify-center mb-10 animate-fade-up"
          style={{ animationDelay: '280ms' }}
        >
          <Link
            to="/login"
            className="btn-primary text-base px-7 py-3 animate-pulse-glow hover:scale-[1.03] transition-transform"
          >
            {t('landingPage.heroCtaTry')}
          </Link>
          <a href="#features" className="btn-outline text-base px-7 py-3 hover:scale-[1.03] transition-transform">
            {t('landingPage.heroCtaFeatures')}
          </a>
        </div>

        <div
          className="flex flex-wrap justify-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-500 animate-fade-up"
          style={{ animationDelay: '380ms' }}
        >
          <span>✓ {t('landingPage.heroPointOffline')}</span>
          <span>✓ {t('landingPage.heroPointPlatforms')}</span>
          <span>✓ {t('landingPage.heroPointNoAds')}</span>
        </div>

        {/* Stats counters */}
        <StatsRow />
      </div>
    </section>
  )
}

function StatsRow() {
  const { t } = useTranslation()
  const stats = [
    { value: 12, suffix: '', label: t('landingPage.statTools') },
    { value: 4, suffix: '', label: t('landingPage.statPlatforms') },
    { value: 100, suffix: '%', label: t('landingPage.statData') },
  ]
  return (
    <div
      className="mt-10 md:mt-14 grid grid-cols-3 gap-2 sm:gap-4 max-w-2xl mx-auto animate-fade-up"
      style={{ animationDelay: '480ms' }}
    >
      {stats.map((s, i) => (
        <div key={i} className="text-center">
          <div className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-primary">
            <Counter to={s.value} />
            {s.suffix}
          </div>
          <div className="text-[11px] sm:text-xs md:text-sm text-gray-500 mt-1 leading-tight">
            {s.label}
          </div>
        </div>
      ))}
    </div>
  )
}

/** Анимированный счётчик 0 → to за ~900ms, запускается при появлении в viewport. */
function Counter({ to }: { to: number }) {
  const [value, setValue] = useState(0)
  const ref = useRef<HTMLSpanElement>(null)
  const started = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true
        const duration = 900
        const start = performance.now()
        const tick = (now: number) => {
          const p = Math.min(1, (now - start) / duration)
          const eased = 1 - (1 - p) ** 3 // easeOutCubic
          setValue(Math.round(to * eased))
          if (p < 1) requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
      }
    })
    io.observe(el)
    return () => io.disconnect()
  }, [to])

  return <span ref={ref}>{value}</span>
}

// ── 2. Features (12 LLM-инструментов) ───────────────────────────────────────

const FEATURES = [
  { emoji: '📋', accent: '#8b5cf6', titleKey: 'landingPage.featureLessonPlanTitle',    descKey: 'landingPage.featureLessonPlanDesc' },
  { emoji: '📝', accent: '#10b981', titleKey: 'landingPage.featureTestTitle',          descKey: 'landingPage.featureTestDesc' },
  { emoji: '📄', accent: '#0ea5e9', titleKey: 'landingPage.featureHandoutTitle',       descKey: 'landingPage.featureHandoutDesc' },
  { emoji: '🎯', accent: '#ef4444', titleKey: 'landingPage.featureRubricTitle',        descKey: 'landingPage.featureRubricDesc' },
  { emoji: '👤', accent: '#f59e0b', titleKey: 'landingPage.featureStudentProfileTitle', descKey: 'landingPage.featureStudentProfileDesc' },
  { emoji: '✉️', accent: '#ec4899', titleKey: 'landingPage.featureParentLetterTitle',  descKey: 'landingPage.featureParentLetterDesc' },
  { emoji: '📊', accent: '#06b6d4', titleKey: 'landingPage.featureTextAdaptTitle',     descKey: 'landingPage.featureTextAdaptDesc' },
  { emoji: '👥', accent: '#a855f7', titleKey: 'landingPage.featureClassPortraitTitle', descKey: 'landingPage.featureClassPortraitDesc' },
  { emoji: '📚', accent: '#f97316', titleKey: 'landingPage.featureSelfEdTitle',        descKey: 'landingPage.featureSelfEdDesc' },
  { emoji: '📈', accent: '#22c55e', titleKey: 'landingPage.featureTermReportTitle',    descKey: 'landingPage.featureTermReportDesc' },
  { emoji: '🔍', accent: '#3b82f6', titleKey: 'landingPage.featureTopicFailTitle',     descKey: 'landingPage.featureTopicFailDesc' },
  { emoji: '📸', accent: '#6366f1', titleKey: 'landingPage.featureVisionTitle',        descKey: 'landingPage.featureVisionDesc' },
]

function FeaturesSection() {
  const { t } = useTranslation()
  return (
    <section id="features" className="py-12 md:py-16 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <SectionHeader
          eyebrow={t('landingPage.featuresEyebrow')}
          title={t('landingPage.featuresTitle')}
          subtitle={t('landingPage.featuresSubtitle')}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10 md:mt-12">
          {FEATURES.map((f, i) => (
            <Reveal key={f.titleKey} delay={(i % 3) * 80}>
              <div
                className="group h-full bg-white border border-purple-100/80 rounded-2xl p-5 transition-all duration-300
                           shadow-[0_2px_16px_-4px_rgba(106,27,154,0.06)]
                           hover:-translate-y-1 hover:border-primary/40
                           hover:shadow-[0_16px_36px_-8px_rgba(106,27,154,0.22)]"
              >
                <IconBadge emoji={f.emoji} accent={f.accent} />
                <h3 className="font-semibold text-gray-900 mt-4 mb-1 group-hover:text-primary transition-colors">
                  {t(f.titleKey)}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">{t(f.descKey)}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-10 text-center text-sm text-gray-500">
          {t('landingPage.featuresExtra')}
        </div>
      </div>
    </section>
  )
}

// ── 3. How it works ─────────────────────────────────────────────────────────

function HowItWorksSection() {
  const { t } = useTranslation()
  const steps = [
    {
      n: '1',
      title: t('landingPage.howStep1Title'),
      desc: t('landingPage.howStep1Desc'),
    },
    {
      n: '2',
      title: t('landingPage.howStep2Title'),
      desc: t('landingPage.howStep2Desc'),
    },
    {
      n: '3',
      title: t('landingPage.howStep3Title'),
      desc: t('landingPage.howStep3Desc'),
    },
  ]

  return (
    <section id="how" className="py-12 md:py-16 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <SectionHeader
          eyebrow={t('landingPage.howEyebrow')}
          title={t('landingPage.howTitle')}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 120}>
              <div className="group relative h-full bg-white rounded-xl p-6 border border-gray-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_32px_-8px_rgba(87,10,166,0.25)] hover:border-primary/40">
                <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-gradient-to-br from-primary to-purple-500 text-white flex items-center justify-center font-bold text-xl shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                  {s.n}
                </div>
                <h3 className="font-semibold text-gray-900 mt-4 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── 4. Advantages ───────────────────────────────────────────────────────────

function AdvantagesSection() {
  const { t } = useTranslation()
  const items = [
    {
      emoji: '🔒',
      accent: '#7c3aed',
      title: t('landingPage.adv1Title'),
      desc: t('landingPage.adv1Desc'),
    },
    {
      emoji: '⚡',
      accent: '#f59e0b',
      title: t('landingPage.adv2Title'),
      desc: t('landingPage.adv2Desc'),
    },
    {
      emoji: '🏫',
      accent: '#0ea5e9',
      title: t('landingPage.adv3Title'),
      desc: t('landingPage.adv3Desc'),
    },
    {
      emoji: '🧩',
      accent: '#ec4899',
      title: t('landingPage.adv4Title'),
      desc: t('landingPage.adv4Desc'),
    },
    {
      emoji: '📑',
      accent: '#10b981',
      title: t('landingPage.adv5Title'),
      desc: t('landingPage.adv5Desc'),
    },
    {
      emoji: '🌐',
      accent: '#6366f1',
      title: t('landingPage.adv6Title'),
      desc: t('landingPage.adv6Desc'),
    },
  ]

  return (
    <section id="advantages" className="py-12 md:py-16 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <SectionHeader
          eyebrow={t('landingPage.advEyebrow')}
          title={t('landingPage.advTitle')}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mt-10 md:mt-12">
          {items.map((item, i) => (
            <Reveal key={item.title} delay={(i % 2) * 100}>
              <div className="group flex gap-4 p-3 rounded-xl transition-all duration-300 hover:bg-primary-soft/30">
                <IconBadge emoji={item.emoji} accent={item.accent} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-primary transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── 5. Pricing ──────────────────────────────────────────────────────────────

function PricingSection() {
  const { t } = useTranslation()
  const plans = [
    {
      name: t('landingPage.planSchoolName'),
      price: t('landingPage.planPriceOnRequest'),
      desc: t('landingPage.planSchoolDesc'),
      features: [
        t('landingPage.planSchoolFeature1'),
        t('landingPage.planSchoolFeature2'),
        t('landingPage.planSchoolFeature3'),
        t('landingPage.planSchoolFeature4'),
        t('landingPage.planSchoolFeature5'),
        t('landingPage.planSchoolFeature6'),
        t('landingPage.planSchoolFeature7'),
        t('landingPage.planSchoolFeature8'),
      ],
      cta: t('landingPage.planCta'),
      ctaLink: 'mailto:info@smart-life.pro?subject=EdPad Учитель для школы',
      featured: true,
    },
    {
      name: t('landingPage.planRegionName'),
      price: t('landingPage.planPriceOnRequest'),
      desc: t('landingPage.planRegionDesc'),
      features: [
        t('landingPage.planRegionFeature1'),
        t('landingPage.planRegionFeature2'),
        t('landingPage.planRegionFeature3'),
        t('landingPage.planRegionFeature4'),
        t('landingPage.planRegionFeature5'),
        t('landingPage.planRegionFeature6'),
      ],
      cta: t('landingPage.planCta'),
      ctaLink: 'mailto:info@smart-life.pro?subject=EdPad Учитель для региона',
      featured: false,
    },
  ]

  return (
    <section id="pricing" className="py-12 md:py-16 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <SectionHeader
          eyebrow={t('landingPage.pricingEyebrow')}
          title={t('landingPage.pricingTitle')}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 md:mt-12 max-w-4xl mx-auto">
          {plans.map((p, i) => (
            <Reveal key={p.name} delay={i * 100}>
            <div
              className={`h-full rounded-2xl p-6 transition-all duration-300 ${
                p.featured
                  ? 'bg-gradient-to-br from-primary to-purple-600 text-white shadow-[0_20px_50px_-15px_rgba(87,10,166,0.5)] md:scale-105 hover:scale-[1.08]'
                  : 'bg-white border border-gray-200 hover:-translate-y-1 hover:shadow-[0_12px_32px_-8px_rgba(87,10,166,0.2)] hover:border-primary/40'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">{p.name}</h3>
                {p.featured && (
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded">{t('landingPage.planPopular')}</span>
                )}
              </div>
              <div className="text-3xl font-extrabold mb-2">{p.price}</div>
              <p className={`text-sm mb-6 ${p.featured ? 'text-white/80' : 'text-gray-600'}`}>
                {p.desc}
              </p>
              <ul className="space-y-2 mb-8 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2">
                    <span>✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <a
                href={p.ctaLink}
                className={`block text-center py-2.5 px-4 rounded-md font-medium transition ${
                  p.featured
                    ? 'bg-white text-primary hover:bg-gray-100'
                    : 'bg-primary text-white hover:bg-primary-dark'
                }`}
              >
                {p.cta}
              </a>
            </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── 6. FAQ ──────────────────────────────────────────────────────────────────

const FAQ = [
  { qKey: 'landingPage.faqQ1', aKey: 'landingPage.faqA1' },
  { qKey: 'landingPage.faqQ2', aKey: 'landingPage.faqA2' },
  { qKey: 'landingPage.faqQ3', aKey: 'landingPage.faqA3' },
  { qKey: 'landingPage.faqQ4', aKey: 'landingPage.faqA4' },
  { qKey: 'landingPage.faqQ5', aKey: 'landingPage.faqA5' },
  { qKey: 'landingPage.faqQ6', aKey: 'landingPage.faqA6' },
  { qKey: 'landingPage.faqQ7', aKey: 'landingPage.faqA7' },
  { qKey: 'landingPage.faqQ8', aKey: 'landingPage.faqA8' },
]

function FaqSection() {
  const { t } = useTranslation()
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section id="faq" className="py-12 md:py-16 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <SectionHeader eyebrow={t('landingPage.faqEyebrow')} title={t('landingPage.faqTitle')} />

        <div className="mt-12 space-y-3">
          {FAQ.map((item, i) => (
            <Reveal key={i} delay={i * 40}>
              <div
                className={`border rounded-lg overflow-hidden transition-all duration-300 ${
                  openIndex === i ? 'border-primary/40 shadow-[0_8px_24px_-12px_rgba(87,10,166,0.3)]' : 'border-gray-200'
                }`}
              >
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition"
                  aria-expanded={openIndex === i}
                >
                  <span className="font-medium text-gray-900">{t(item.qKey)}</span>
                  <span
                    className={`text-primary text-2xl transition-transform duration-300 ${openIndex === i ? 'rotate-45' : ''}`}
                  >
                    +
                  </span>
                </button>
                <div
                  className={`grid transition-all duration-300 ease-out ${
                    openIndex === i ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed">{t(item.aKey)}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── 7. CTA + Footer ─────────────────────────────────────────────────────────

function CtaSection() {
  const { t } = useTranslation()
  return (
    <section className="relative py-14 md:py-20 overflow-hidden bg-gradient-to-br from-primary to-primary-dark text-white text-center">
      {/* Animated blobs in CTA */}
      <div className="absolute top-1/4 left-10 w-80 h-80 bg-purple-400/20 rounded-full blur-3xl animate-blob-slow" />
      <div
        className="absolute bottom-0 right-10 w-72 h-72 bg-fuchsia-300/15 rounded-full blur-3xl animate-blob-slow"
        style={{ animationDelay: '-5s' }}
      />
      <Reveal className="relative max-w-3xl mx-auto px-4 sm:px-6">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('landingPage.ctaTitle')}</h2>
        <p className="text-lg text-white/90 mb-8">
          {t('landingPage.ctaSubtitle')}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/login"
            className="bg-white text-primary font-semibold px-8 py-3 rounded-md hover:bg-gray-100 transition-all hover:scale-[1.03] shadow-lg"
          >
            {t('landingPage.ctaStartFree')}
          </Link>
          <a
            href="mailto:info@smart-life.pro"
            className="border border-white/40 text-white px-8 py-3 rounded-md hover:bg-white/10 transition-all hover:scale-[1.03]"
          >
            {t('landingPage.ctaRequestDemo')}
          </a>
        </div>
      </Reveal>
    </section>
  )
}

// ── Shared section header ───────────────────────────────────────────────────

function SectionHeader({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string
  title: string
  subtitle?: string
}) {
  return (
    <div className="text-center max-w-3xl mx-auto">
      <div className="text-sm font-semibold text-primary uppercase tracking-wider mb-2">
        {eyebrow}
      </div>
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{title}</h2>
      {subtitle && <p className="text-gray-600 mt-4 text-lg">{subtitle}</p>}
    </div>
  )
}
